class NoahsPopupsProManager {
  constructor(settings) {
    this.settings = settings;
    this.sessionStarted = Number(sessionStorage.getItem('noahsPopupProSessionStarted')) || Date.now();
    sessionStorage.setItem('noahsPopupProSessionStarted', this.sessionStarted);
    this.pageViews = Number(sessionStorage.getItem('noahsPopupProPageViews') || 0) + 1;
    sessionStorage.setItem('noahsPopupProPageViews', this.pageViews);
  }

  parse(popup) {
    try { return typeof popup.popup_settings === 'string' ? JSON.parse(popup.popup_settings || '{}') : (popup.popup_settings || {}); }
    catch (error) { return {}; }
  }

  enabled(value) { return [true, 'true', 1].includes(value); }
  device() { return window.innerWidth < 768 ? 'mobile' : (window.innerWidth < 1025 ? 'tablet' : 'desktop'); }

  history(id) {
    try { return JSON.parse(localStorage.getItem(`noahsPopupProViews_${id}`) || '[]'); }
    catch (error) { return []; }
  }

  eligible(popup) {
    const pro = this.parse(popup).pro || {};
    const now = Date.now();
    const start = pro.schedule?.start ? Date.parse(pro.schedule.start) : 0;
    const end = pro.schedule?.end ? Date.parse(pro.schedule.end) : 0;
    if ((start && now < start) || (end && now > end)) return false;
    const target = pro.audience?.device || 'all';
    if (target !== 'all' && target !== this.device()) return false;
    const userStatus = pro.audience?.user_status || 'all';
    if (userStatus === 'authenticated' && !this.settings.noahs_popups_pro?.loggedIn) return false;
    if (userStatus === 'anonymous' && this.settings.noahs_popups_pro?.loggedIn) return false;
    const requiredRoles = String(pro.audience?.roles || '').split(',').map((role) => role.trim()).filter(Boolean);
    if (requiredRoles.length && !requiredRoles.some((role) => (this.settings.noahs_popups_pro?.roles || []).includes(role))) return false;
    if (pro.audience?.url_contains && !window.location.href.includes(pro.audience.url_contains)) return false;
    if (pro.audience?.utm_source && new URLSearchParams(window.location.search).get('utm_source') !== pro.audience.utm_source) return false;
    const requiredSeconds = Number.parseInt(pro.audience?.session_seconds || 0, 10);
    if (requiredSeconds > 0 && Date.now() - this.sessionStarted < requiredSeconds * 1000) return false;
    const requiredViews = Number.parseInt(pro.audience?.page_views || 0, 10);
    if (requiredViews > 0 && this.pageViews < requiredViews) return false;
    const weekdays = String(pro.schedule?.weekdays || '').split(',').map(day => day.trim()).filter(Boolean).map(Number).filter(day => Number.isInteger(day) && day >= 0 && day <= 6);
    if (weekdays.length && !weekdays.includes(new Date().getDay())) return false;
    const currentTime = new Date().toTimeString().slice(0, 5);
    if (pro.schedule?.daily_start && currentTime < pro.schedule.daily_start) return false;
    if (pro.schedule?.daily_end && currentTime > pro.schedule.daily_end) return false;
    const max = Number.parseInt(pro.frequency?.max_views || 0, 10);
    const days = Number.parseInt(pro.frequency?.period_days || 30, 10);
    const cutoff = now - days * 86400000;
    return max <= 0 || this.history(popup.noahs_id).filter((time) => time >= cutoff).length < max;
  }

  record(popup, event, reason = '') {
    const config = this.settings.noahs_popups_pro || {};
    if (!this.enabled(this.parse(popup).pro?.analytics?.enabled) || !config.eventUrl) return;
    fetch(config.eventUrl, {
      method: 'POST', credentials: 'same-origin', keepalive: true,
      headers: {'Content-Type': 'application/json', 'X-CSRF-Token': config.eventToken},
      body: JSON.stringify({noahs_id: popup.noahs_id, event, reason, langcode: document.documentElement.lang || ''}),
    }).catch(() => {});
  }

  opened({popup, element}) {
    const animation = this.parse(popup).pro?.animation || {};
    const duration = Math.max(100, Math.min(3000, Number.parseInt(animation.duration || 350, 10)));
    element.style.setProperty('--noahs-popup-animation-duration', `${duration}ms`);
    element.classList.add(`noahs-popup-pro-entry-${animation.entry || 'fade'}`);
    element.dataset.noahsExitClass = `noahs-popup-pro-exit-${animation.exit || 'fade'}`;
    element.dataset.noahsExitDuration = duration;
    const views = this.history(popup.noahs_id);
    views.push(Date.now());
    localStorage.setItem(`noahsPopupProViews_${popup.noahs_id}`, JSON.stringify(views.slice(-100)));
    this.record(popup, 'open', 'trigger');
    const conversionSelector = this.parse(popup).pro?.analytics?.conversion_selector;
    if (conversionSelector) {
      const conversionHandler = (event) => {
        try {
          if (!event.target.closest(conversionSelector)) return;
          this.record(popup, 'conversion', 'selector_click');
          element.removeEventListener('click', conversionHandler);
        }
        catch (error) {}
      };
      element.addEventListener('click', conversionHandler);
    }
  }

  install(manager) {
    if (manager._noahsProInstalled) return;
    manager._noahsProInstalled = true;
    Object.values(manager.popups).forEach((popup) => {
      const triggers = this.parse(popup).pro?.triggers || {};
      const open = () => manager.shouldShowPopup(popup) && manager.loadPopup(popup);
      if (this.enabled(triggers.exit_intent?.enabled)) {
        const handler = ({clientY}) => { if (clientY <= 5) { document.removeEventListener('mouseout', handler); open(); } };
        document.addEventListener('mouseout', handler);
      }
      const inactivity = Number.parseInt(triggers.inactivity?.seconds || 0, 10);
      if (inactivity > 0) window.setTimeout(open, inactivity * 1000);
      const sessionSeconds = Number.parseInt(this.parse(popup).pro?.audience?.session_seconds || 0, 10);
      if (sessionSeconds > 0) {
        const remaining = Math.max(0, sessionSeconds * 1000 - (Date.now() - this.sessionStarted));
        window.setTimeout(open, remaining);
      }
      const percent = Number.parseInt(triggers.scroll_percent?.percent || 0, 10);
      if (percent > 0) {
        const handler = () => {
          const available = document.documentElement.scrollHeight - window.innerHeight;
          if (available <= 0 || window.scrollY / available * 100 < percent) return;
          window.removeEventListener('scroll', handler); open();
        };
        window.addEventListener('scroll', handler, {passive: true}); handler();
      }
      const selector = triggers.click?.selector;
      if (selector) document.addEventListener('click', (event) => {
        try { if (event.target.closest(selector)) { event.preventDefault(); open(); } }
        catch (error) {}
      });
    });
  }
}

const proManager = new NoahsPopupsProManager(drupalSettings);
document.addEventListener('noahs:popup-eligibility', ({detail}) => { if (detail.allowed && !proManager.eligible(detail.popup)) detail.allowed = false; });
document.addEventListener('noahs:popup-opened', ({detail}) => proManager.opened(detail));
document.addEventListener('noahs:popup-closed', ({detail}) => proManager.record(detail.popup, 'close', detail.reason));
document.addEventListener('noahs:popups-ready', ({detail}) => proManager.install(detail.manager));
Drupal.behaviors.noahsPopupsPro = { attach(context) { once('noahs-popups-pro', 'body', context).forEach(() => window.NoahsPopupManager && proManager.install(window.NoahsPopupManager)); } };
